/**
 * AdvisorPanel.tsx — AI Strategy Advisor chat slide-over (T-501, design §1)
 *
 * A right-edge slide-over modeled on InfoSidePanel (same anchor/idiom, Escape-to-close, teal
 * top stripe). It owns the transcript + status, assembles CONTEXT via the injected getContext()
 * at send time, and sends via the injected sendChat() (defaults to the real advisorChat). All
 * §1.3 states live here: empty (greeting + example prompts, or a focus-first nudge when nothing
 * is selected), loading (Send disabled + live partial), reply, and error/rate-limited with Retry.
 *
 * Transport is transparent (T-524): the panel passes `onPartial` to sendChat, which streams tokens
 * over SSE when the server offers it and falls back to a single buffered JSON body when it does
 * not. Either way sendChat resolves with the AUTHORITATIVE full reply, which is what lands in the
 * transcript — so a reply the server corrected at stream end (T-523's numeric audit) replaces what
 * was rendered rather than sitting next to it.
 *
 * Auto-scroll is stick-to-bottom-only-when-at-bottom (T-544): new content pulls the view down only
 * while the user is already parked at the bottom, so scrolling up to re-read something is never
 * fought by the next token. Sending re-arms it.
 *
 * Streamed text lives in `partial`, deliberately OUTSIDE the transcript, until the turn completes.
 * An interrupted reply therefore stays on screen (marked incomplete) without ever being replayed to
 * the model as a finished assistant turn.
 */
import { useEffect, useRef, useState } from 'react'
import { X, MessageSquareText } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Spinner } from './ui/spinner'
import { advisorChat as defaultSendChat, AdvisorError, MAX_MESSAGE_CHARS } from '@/lib/advisorChat'
import type { AdvisorReply } from '@/lib/advisorChat'
import type { AdvisorContext, AdvisorMessage } from '@/lib/advisorContext'

const EXAMPLE_PROMPTS = [
  'Why does this area score the way it does?',
  'Where should I look to expand near here?',
  'What patterns should I watch in this view?',
]

const EMPTY_CONTEXT_HINT = 'Search an address or select a tract to get grounded advice about it.'
const RATE_LIMITED_COPY = "You're sending messages quickly — give it a moment and try again."
const DAILY_LIMIT_COPY = "You've reached today's advisor limit. It resets tomorrow."
const UNAVAILABLE_COPY = 'The advisor is unavailable right now. Please try again in a moment.'
const GENERIC_ERROR_COPY = 'Something went wrong. Please try again.'
// Deliberately does NOT restate that the partial is incomplete — the partial carries its own inline
// marker, and saying it twice reads as two separate problems.
const STREAM_INTERRUPTED_COPY = 'The advisor was interrupted before it finished. Please try again.'
// T-548. Deliberately worded like the interrupted marker above and shown the same way, because to
// the reader it is the same problem — the answer stops early — and only the CAUSE differs. It says
// "cut short" rather than "interrupted" so the two are still distinguishable in a bug report.
const TRUNCATED_COPY = 'Cut short — the advisor reached its length limit.'

/**
 * How close to the bottom (px) still counts as "at the bottom" (T-544).
 *
 * Not zero, deliberately. `scrollHeight - scrollTop - clientHeight` is rarely exactly 0 in a real
 * browser: fractional device pixels, browser zoom and sub-pixel layout all leave a pixel or two on
 * the table. An exact comparison would read "the user scrolled away" while they are visibly pinned
 * to the bottom, and auto-scroll would silently stop following.
 */
export const STICK_TO_BOTTOM_THRESHOLD_PX = 32

/** Extracted and exported so the decision is unit-testable without a layout engine. */
export function isAtBottom(
  el: Pick<HTMLElement, 'scrollTop' | 'clientHeight' | 'scrollHeight'>,
  threshold: number = STICK_TO_BOTTOM_THRESHOLD_PX,
): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold
}

type SendChat = (
  messages: AdvisorMessage[],
  context: AdvisorContext,
  options?: { onPartial?: (accumulated: string) => void },
) => Promise<AdvisorReply>

/**
 * A transcript entry plus the client-only facts about how the turn ended (T-548).
 *
 * `truncated` is tracked PER MESSAGE, not just for the newest reply: a cut-off answer keeps its
 * marker as the conversation grows, so scrolling back never shows an unexplained mid-word stop.
 * It stays out of `AdvisorMessage` because that type is the wire shape sent back to the server —
 * this is presentation state, and `toWire` below strips it before any request.
 */
type TranscriptEntry = AdvisorMessage & { truncated?: boolean }

/** Send only the wire fields the server's schema declares — never our presentation state. */
function toWire(entries: TranscriptEntry[]): AdvisorMessage[] {
  return entries.map((m) => ({ role: m.role, content: m.content }))
}

export interface AdvisorPanelProps {
  open: boolean
  onClose: () => void
  /** Assembles the current CONTEXT at send time (design §3.3), closed over live map state. */
  getContext: () => AdvisorContext
  /** Injected for tests; defaults to the real client. */
  sendChat?: SendChat
}

function errorCopy(err: unknown): string {
  if (err instanceof AdvisorError) {
    // A mid-stream failure carries status 200 — the headers were already sent when it opened — so
    // it is identified by scope, and must be checked before any status-based branch.
    if (err.scope === 'stream') return STREAM_INTERRUPTED_COPY
    if (err.status === 429) return err.scope === 'daily' ? DAILY_LIMIT_COPY : RATE_LIMITED_COPY
    if (err.status === 503 || err.status === 502) return UNAVAILABLE_COPY
  }
  return GENERIC_ERROR_COPY
}

export function AdvisorPanel({
  open,
  onClose,
  getContext,
  sendChat = defaultSendChat,
}: AdvisorPanelProps) {
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([])
  const [input, setInput] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending'>('idle')
  const [error, setError] = useState<string | null>(null)
  // Streamed text for the turn in flight. Kept out of `transcript` on purpose — see the file header.
  const [partial, setPartial] = useState<string | null>(null)
  const [partialIncomplete, setPartialIncomplete] = useState(false)
  const lastSentRef = useRef<TranscriptEntry[] | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  // Whether new content should pull the view down (T-544). A REF, not state: this updates on every
  // scroll event and every streamed token, and none of those should re-render the transcript.
  // Starts true — an empty panel is already at its bottom.
  const stickToBottomRef = useRef(true)

  // Follow streamed text ONLY while the user is parked at the bottom. The whole point of the flag
  // is that a user who scrolled up to re-read something is mid-thought: yanking them back down on
  // the next token makes the panel unusable while a reply streams, which is worse than not
  // following at all. Runs on `partial` too, so it tracks token by token rather than once per turn.
  useEffect(() => {
    if (!stickToBottomRef.current) return
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [transcript, partial, status])

  // Re-evaluated from the DOM on every scroll — whether the scroll came from the user or from the
  // effect above. That self-correction is what lets a user rejoin the bottom and resume following
  // without any extra bookkeeping.
  function handleScroll() {
    const el = scrollRef.current
    if (el) stickToBottomRef.current = isAtBottom(el)
  }

  // Escape closes — only while open (so a closed panel doesn't swallow the key).
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  function runSend(messages: TranscriptEntry[]) {
    lastSentRef.current = messages
    setStatus('sending')
    setError(null)
    setPartial(null)
    setPartialIncomplete(false)
    sendChat(toWire(messages), getContext(), { onPartial: setPartial })
      .then((reply) => {
        // The resolved reply is authoritative and replaces whatever streamed. A reply that ran out
        // of room is kept — it is a real, usable partial answer — but it is stored WITH the fact
        // that it stopped early, so it can never be rendered as if it had finished (T-548).
        setTranscript((m) => [
          ...m,
          { role: 'assistant', content: reply.text, truncated: reply.truncated },
        ])
        setPartial(null)
        setStatus('idle')
      })
      .catch((err) => {
        setStatus('idle')
        setError(errorCopy(err))
        // Keep a partial that was interrupted: discarding text the user already read helps nobody.
        // It stays out of the transcript, so the next turn never replays a truncated answer.
        if (err instanceof AdvisorError && err.scope === 'stream') setPartialIncomplete(true)
        else setPartial(null)
      })
  }

  function send(text: string) {
    const content = text.trim()
    if (!content || status === 'sending') return
    // Submitting is an explicit request to see what happens next, so it re-arms following even if
    // the user had scrolled up. Without this, a user who read back and then asked a question would
    // watch their own message and its answer arrive off-screen.
    stickToBottomRef.current = true
    const next = [...transcript, { role: 'user' as const, content }]
    setTranscript(next)
    setInput('')
    runSend(next)
  }

  function retry() {
    if (lastSentRef.current) runSend(lastSentRef.current)
  }

  const ctx = getContext()
  const focused = Boolean(ctx.activeTract || ctx.focus)
  const isEmpty = transcript.length === 0

  return (
    <div
      role="complementary"
      aria-label="AI Strategy Advisor"
      aria-hidden={!open}
      className={cn(
        'fixed top-12 right-0 bottom-0 z-20 w-[420px] max-w-full',
        'bg-card border-l border-border shadow-xl',
        'flex flex-col',
        'transition-transform duration-200 ease-in-out',
        open ? 'translate-x-0' : 'translate-x-full',
      )}
    >
      {/* Header */}
      <div className="h-12 flex-shrink-0 flex items-center justify-between px-4 border-b border-border border-t-2 border-t-primary">
        <div className="flex items-center gap-2 min-w-0">
          <MessageSquareText className="h-4 w-4 text-primary flex-shrink-0" />
          <span className="text-sm font-semibold truncate">Strategy Advisor</span>
        </div>
        <button
          onClick={onClose}
          aria-label="Close advisor"
          className="ml-2 flex-shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground
                     hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2
                     focus-visible:ring-ring min-h-[36px] min-w-[36px] flex items-center justify-center"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Transcript / empty state */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="oy-scroll flex-1 overflow-y-auto px-4 py-3 space-y-3 text-sm"
      >
        {isEmpty ? (
          <div className="space-y-3 text-muted-foreground">
            <p>
              Try asking about the area you're viewing — I ground every answer in what's on your
              map.
            </p>
            {!focused && <p className="text-xs">{EMPTY_CONTEXT_HINT}</p>}
            <div className="space-y-2">
              {EXAMPLE_PROMPTS.map((p) => (
                <button
                  key={p}
                  onClick={() => send(p)}
                  className="block w-full text-left rounded-md border border-border px-3 py-2 text-xs
                             hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        ) : (
          transcript.map((m, i) => (
            <div
              key={i}
              className={cn(
                'rounded-lg px-3 py-2 max-w-[85%] whitespace-pre-wrap',
                m.role === 'user' ? 'ml-auto bg-primary/10' : 'mr-auto bg-muted',
              )}
            >
              {m.content}
              {m.truncated && (
                <p className="mt-1 text-xs italic text-muted-foreground">{TRUNCATED_COPY}</p>
              )}
            </div>
          ))
        )}

        {/* Live streamed reply, or the interrupted remains of one. */}
        {partial !== null && partial.length > 0 && (
          <div className="mr-auto max-w-[85%] rounded-lg bg-muted px-3 py-2 whitespace-pre-wrap">
            {partial}
            {partialIncomplete && (
              <p className="mt-1 text-xs italic text-muted-foreground">
                Incomplete — the advisor was interrupted.
              </p>
            )}
          </div>
        )}

        {/* Thinking indicator — only until the first token lands, then the real text takes over. */}
        {status === 'sending' && !partial && (
          <div className="mr-auto max-w-[85%] rounded-lg bg-muted px-3 py-2">
            <span className="flex items-center gap-2 text-muted-foreground text-xs">
              <Spinner size={14} label="Thinking" /> Thinking…
            </span>
          </div>
        )}

        {error && (
          <div className="mr-auto max-w-[85%] rounded-lg bg-destructive/10 text-destructive px-3 py-2 text-xs">
            <p>{error}</p>
            <button
              onClick={retry}
              className="mt-1 underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              Retry
            </button>
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="flex-shrink-0 border-t border-border p-3 flex items-end gap-2">
        <input
          type="text"
          value={input}
          maxLength={MAX_MESSAGE_CHARS}
          disabled={status === 'sending'}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              send(input)
            }
          }}
          placeholder="Ask the advisor…"
          aria-label="Message the advisor"
          className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm
                     text-foreground placeholder:text-muted-foreground focus-visible:outline-none
                     focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
        />
        <button
          onClick={() => send(input)}
          disabled={status === 'sending'}
          className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground
                     hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2
                     focus-visible:ring-ring disabled:opacity-60"
        >
          Send
        </button>
      </div>
    </div>
  )
}
